// slashCommand — Phase 7 Task 7-9: parser + dispatcher for `/help`, `/rom`,
// `/clear`, `/imagepaste`.
//
// Phase 6.7 サブ task 後追い (consult 2026-05-07): `/bellon` (= 入室お知らせ
// ベル切替) を追加. **唯一の引数を取るコマンド**で、`/bellon roomin` /
// `/bellon off` のサブコマンド形式. parser は他コマンドの「引数があれば警告」
// と分けるため、`bellon` を `extra-args` 経路に倒さず別 kind (`'bellon'`)
// で返す. dispatcher 側は専用 module `application/bellOnRoomin.ts` に委譲.
//
// Design:
//   - parsing is a pure function (`parseSlashCommand`) returning a
//     discriminated union; the React layer only needs to map the result to
//     side-effects via `dispatchSlashCommand`. This keeps the parser unit-
//     testable without a DOM and lets the dispatcher orchestrate the
//     side-effects (toggle ROM / clear chat log / open file picker / push
//     help lines) through injected callbacks.
//   - 4 of the 5 commands take **no arguments** (Phase 7 Task 7-9 確定方針).
//     If the user passes extra tokens (e.g. `/help foo`) we run the command
//     anyway and push a brief warning system line so they get feedback
//     without losing the UX.
//   - `/bellon` is the **唯一 argument を取るコマンド** (= sub-command 形式).
//     parser は `kind: 'bellon'` で args 文字列を `BellonSubCommand` 解釈
//     済の形で返し、dispatcher が `bellOnRoomin.ts` に委譲する.
//   - `/open tool` / `/close tool` / `/open chat` / `/close chat` (floating-panels-
//     2026-08-01.md §12): 旧 Java 版 `OekakichatClientPanel.openToolFrame()` /
//     `closeToolFrame()` のソース確認で判明した実挙動 (paintToolCanvas +
//     colorMakeCanvas + maskSetCanvas を**一体で**フロート化、`/close tool` で
//     元へ戻す。`/open chat`/`/close chat` も対の存在) に合わせたパリティ回復。
//     `open`/`close` は `bellon` と同じく args を取るコマンドなので同じ分岐に
//     相乗りする (NO_ARG_COMMANDS 集合には入れない)。
//   - `/clear` clears the **local chat log only** (chat + system). It is NOT
//     a canvas clear — that lives on the ToolPanel「全消し」button (Task 7-5).
//     Documented intent: 旧 Java applet の `/clear` 互換ではなく、現代的な
//     IRC 系クライアントの「ローカルログ消去」セマンティクスに揃える
//     (architecture.md §16 周辺の確定方針).
//   - `/name` slash command は **意図的に未実装**: Phase 7 では再入室経路で
//     名前を変える方針 (`sendNameChange` API は残置するが UI 露出は無し).
//   - `/kick` slash command も意図的に未実装: 参加者リスト UI の kick ボタンに
//     集約 (Task 7-9).
//
// (architecture.md §16, docs/_wip/protocol-events.md §6)

import { type BellonSubCommand, dispatchBellonCommand, parseBellonArgs } from './bellOnRoomin.js';

/**
 * Recognised command names. Single source of truth for the parser + the
 * `/help` dispatcher (so they can never drift apart).
 *
 * `'bellon'` (Phase 6.7 サブ task 後追い、consult 2026-05-07) は唯一の
 * argument を取るコマンド. parser 内で別 branch で扱う.
 */
export const SLASH_COMMAND_NAMES = [
  'help',
  'rom',
  'clear',
  'imagepaste',
  'bellon',
  'open',
  'close',
] as const;
export type SlashCommandName = (typeof SLASH_COMMAND_NAMES)[number];

/** No-arg コマンド (= `'bellon'` を除いた集合). 'extra-args' 警告対象. */
const NO_ARG_COMMANDS: ReadonlyArray<SlashCommandName> = ['help', 'rom', 'clear', 'imagepaste'];
const isNoArgCommand = (name: SlashCommandName): boolean =>
  (NO_ARG_COMMANDS as readonly string[]).includes(name);

/** Output of `parseSlashCommand`. */
export type ParsedSlashCommand =
  /** Input doesn't start with `/` — caller should send as a normal chat. */
  | { readonly kind: 'not-slash' }
  /** Input is just `/` (or `/` + whitespace) with no command name. */
  | { readonly kind: 'empty' }
  /** Recognised no-arg command. */
  | { readonly kind: 'ok'; readonly name: Exclude<SlashCommandName, 'bellon' | 'open' | 'close'> }
  /**
   * Recognised no-arg command, but extra tokens were supplied. Callers SHOULD
   * run the command and push a warning line (寛容戦略).
   */
  | {
      readonly kind: 'extra-args';
      readonly name: Exclude<SlashCommandName, 'bellon' | 'open' | 'close'>;
      readonly args: string;
    }
  /**
   * `/bellon ...` — sub-command 形式 (`roomin` で toggle、`off` で明示 OFF).
   * parser がここで `parseBellonArgs` を使ってサブを解決済みで返す.
   */
  | { readonly kind: 'bellon'; readonly sub: BellonSubCommand }
  /**
   * `/open ...` (floating-panels-2026-08-01.md §12) — 旧 Java 版
   * `openToolFrame()`/`openChatFrame()` パリティ. `target==='tool'|'chat'`
   * のときのみ実行、それ以外 (未知 sub-command / 引数なし) は使い方ヒントを出す
   * (= `/bellon` の不正 sub-command と同じ「実行せず案内」寛容戦略).
   */
  | { readonly kind: 'open'; readonly target: OpenCloseCommandTarget }
  /**
   * `/close ...` (floating-panels-2026-08-01.md §12) — `/open` の対。
   * 旧 Java 版 `closeToolFrame()`/`closeChatFrame()` パリティ。
   */
  | { readonly kind: 'close'; readonly target: OpenCloseCommandTarget }
  /** Unknown command name. */
  | { readonly kind: 'unknown'; readonly name: string };

/**
 * `/open` / `/close` の既知ターゲット (= `tool` = Classic サイドバー全体
 * (旧 Java 版 `paintToolFrame` 相当) / `chat` = チャット+メンバー)。
 */
export type OpenCloseCommandTarget = 'tool' | 'chat' | 'unknown';

const parseOpenCloseArgs = (raw: string): OpenCloseCommandTarget => {
  const t = raw.trim().toLowerCase();
  if (t === 'tool') return 'tool';
  if (t === 'chat') return 'chat';
  return 'unknown';
};

/**
 * Parse a chat draft into a `ParsedSlashCommand`.
 *
 * - Leading whitespace is **not** stripped — `' /help'` is treated as a
 *   regular chat (callers `.trim()` before sending; if they pre-trim and the
 *   first char is `/`, that's a command).
 * - Command name matching is case-insensitive. The protocol layer never sees
 *   slash commands (they are all client-local), so casing is purely a UX call.
 * - Returns `not-slash` for any input that doesn't begin with `/`.
 */
export const parseSlashCommand = (text: string): ParsedSlashCommand => {
  if (!text.startsWith('/')) return { kind: 'not-slash' };
  // Drop the leading slash, split on whitespace.
  const rest = text.slice(1);
  // Empty / whitespace-only command: the user typed `/` and Enter.
  if (rest.trim().length === 0) return { kind: 'empty' };
  // Find the first whitespace to separate the name from arguments. We use a
  // regex over `\s` so non-ASCII whitespace (idiomatic Japanese full-width
  // space U+3000) is treated as a separator too — pasting something through
  // an IME shouldn't accidentally embed the command name.
  const match = rest.match(/^(\S+)(\s+(.*))?$/);
  // Defensive — `rest.trim().length > 0` guarantees at least one non-space
  // char, so `match` is always truthy here. The fallback keeps the type
  // checker happy without an extra runtime branch.
  if (match === null) return { kind: 'empty' };
  const rawName = (match[1] ?? '').toLowerCase();
  const rawArgs = (match[3] ?? '').trim();

  if (!isKnownCommand(rawName)) {
    return { kind: 'unknown', name: rawName };
  }
  // `/bellon` / `/open` / `/close` は args を取る. サブコマンドを解決して別 kind で返す.
  if (rawName === 'bellon') {
    return { kind: 'bellon', sub: parseBellonArgs(rawArgs) };
  }
  if (rawName === 'open') {
    return { kind: 'open', target: parseOpenCloseArgs(rawArgs) };
  }
  if (rawName === 'close') {
    return { kind: 'close', target: parseOpenCloseArgs(rawArgs) };
  }
  if (rawArgs.length > 0) {
    return { kind: 'extra-args', name: rawName, args: rawArgs };
  }
  if (!isNoArgCommand(rawName)) {
    // unreachable defensively (= 'bellon'/'open'/'close' は上で分岐済).
    return { kind: 'unknown', name: rawName };
  }
  return { kind: 'ok', name: rawName };
};

const isKnownCommand = (name: string): name is SlashCommandName =>
  (SLASH_COMMAND_NAMES as readonly string[]).includes(name);

// ─── Side-effect dispatcher ──────────────────────────────────────────────────

/**
 * Side-effect ports for slash commands. Callers (ChatPanel) inject these
 * concrete callbacks that wire to the SessionProvider / hooks.
 *
 * `imagepasteStart` may be `null` — that is the signal that the local user
 * lacks `Perm.IMAGEPASTE` (or the canvas isn't mounted yet). The dispatcher
 * gracefully degrades to a system line in that case so the user gets feedback
 * instead of a silent no-op.
 *
 * `setRom` receives the next desired value (= `!isRom`). The caller resolves
 * the toggle direction from `state.isRom` because the slash-command parser is
 * stateless.
 */
export interface SlashCommandPorts {
  /** Append a free-form line to `view.system`. */
  readonly pushSystemLine: (text: string) => void;
  /** Toggle ROM (caller passes `!state.isRom`). */
  readonly setRom: (rom: boolean) => void;
  /** ROM 値の現在値 (toggle direction を決める). */
  readonly currentIsRom: boolean;
  /** Clear the local chat log (chat + system). */
  readonly clearChatLog: () => void;
  /**
   * Open the OS file picker (image/png|jpeg|gif). Pass `null` when the user
   * lacks `Perm.IMAGEPASTE` so the dispatcher can show a graceful warning.
   */
  readonly imagepasteStart: (() => void) | null;
  /**
   * Phase 6.7 サブ task 後追い (consult 2026-05-07): `/bellon roomin`
   * (= 入室お知らせベル切替) 用 ports.
   * - `currentBellOnRoomin`: 現在の ON/OFF (toggle direction を決める).
   * - `setBellOnRoomin`: 次の ON/OFF を伝える callback. caller は
   *   localStorage 永続化と `room.member-joined` listener の起動/停止を行う.
   */
  readonly currentBellOnRoomin: 'on' | 'off';
  readonly setBellOnRoomin: (next: 'on' | 'off') => void;
  /**
   * floating-panels-2026-08-01.md §12: `/open tool` — レイアウトの「ツール一式」
   * をフロート表示にする (旧 Java 版 `openToolFrame()` パリティ回復)。Classic では
   * サイドバー全体 (ToolPanel 含む) が一体で浮く。既にフロート中なら idempotent
   * (= 呼んでも状態は変わらない、caller 側で判定)。
   */
  readonly openToolFloat: () => void;
  /** `/close tool` — `openToolFloat` の対。ドックへ戻す (= ⇤ドックボタンと同じ経路)。 */
  readonly closeToolFloat: () => void;
  /** `/open chat` — チャット+メンバーをフロート表示にする (= 既存の detach 機能を呼ぶだけ)。 */
  readonly openChatFloat: () => void;
  /** `/close chat` — `openChatFloat` の対。ドックへ戻す。 */
  readonly closeChatFloat: () => void;
}

/**
 * Help text shown by `/help`. Order matches `SLASH_COMMAND_NAMES`. Each
 * entry is printed on its own system line so the chat panel's `* `-prefixed
 * styling renders cleanly without monospace alignment hacks.
 */
export const HELP_LINES: ReadonlyArray<string> = [
  '/help            ヘルプを表示',
  '/rom             ROM (見るだけ) の ON / OFF を切替',
  '/clear           チャット欄をクリア (キャンバスは消えません)',
  '/imagepaste      画像をキャンバスに貼り付け',
  '/bellon roomin   入室お知らせベル ON/OFF (再実行で OFF、/bellon off 明示)',
  '/open tool       ツール一式をフロート表示 (旧 Java 版パリティ)',
  '/close tool      フロート中のツール一式をドックへ戻す',
  '/open chat       チャット+メンバーをフロート表示',
  '/close chat      フロート中のチャット+メンバーをドックへ戻す',
];

/**
 * Run a parsed slash command against the supplied side-effect ports.
 *
 * Returns the same parsed result so callers can branch on `kind` for
 * UI-side reactions (e.g. clearing the input box only on `'ok'` /
 * `'extra-args'`). For `'not-slash'` the dispatcher is a no-op — callers
 * should send the text as a normal chat.
 */
export const dispatchSlashCommand = (
  parsed: ParsedSlashCommand,
  ports: SlashCommandPorts,
): ParsedSlashCommand => {
  switch (parsed.kind) {
    case 'not-slash':
      return parsed;
    case 'empty':
      ports.pushSystemLine('コマンドを指定してください (詳細は /help)');
      return parsed;
    case 'unknown':
      ports.pushSystemLine(`未知のコマンドです: /${parsed.name} (詳細は /help)`);
      return parsed;
    case 'extra-args':
      // 寛容戦略: コマンドは実行しつつ、引数があった旨を警告する.
      ports.pushSystemLine(`/${parsed.name} は引数を取りません (無視: "${parsed.args}")`);
      runRecognizedCommand(parsed.name, ports);
      return parsed;
    case 'ok':
      runRecognizedCommand(parsed.name, ports);
      return parsed;
    case 'bellon': {
      // Phase 6.7 サブ task 後追い: `/bellon roomin` / `/bellon off` を専用
      // module に委譲. 状態遷移 + system line を一括で受け取って配線する.
      const result = dispatchBellonCommand(ports.currentBellOnRoomin, parsed.sub);
      if (result.nextState !== ports.currentBellOnRoomin) {
        ports.setBellOnRoomin(result.nextState);
      }
      ports.pushSystemLine(result.systemLine);
      return parsed;
    }
    case 'open': {
      // floating-panels-2026-08-01.md §12: `tool`/`chat` のみ既知. それ以外
      // (未知 sub-command / 引数なし) は実行せず使い方を案内する
      // (= `/bellon` の不正 sub-command と同じ寛容戦略)。
      if (parsed.target === 'tool') {
        ports.openToolFloat();
        ports.pushSystemLine(
          'ツール一式をフロート表示にしました (⇤ドック / /close tool / 表示設定の「配置をリセット」で戻せます)',
        );
      } else if (parsed.target === 'chat') {
        ports.openChatFloat();
        ports.pushSystemLine('チャット+メンバーをフロート表示にしました (/close chat で戻せます)');
      } else {
        ports.pushSystemLine('使い方: /open tool または /open chat');
      }
      return parsed;
    }
    case 'close': {
      if (parsed.target === 'tool') {
        ports.closeToolFloat();
        ports.pushSystemLine('ツール一式をドックへ戻しました');
      } else if (parsed.target === 'chat') {
        ports.closeChatFloat();
        ports.pushSystemLine('チャット+メンバーをドックへ戻しました');
      } else {
        ports.pushSystemLine('使い方: /close tool または /close chat');
      }
      return parsed;
    }
  }
};

const runRecognizedCommand = (
  name: Exclude<SlashCommandName, 'bellon' | 'open' | 'close'>,
  ports: SlashCommandPorts,
): void => {
  switch (name) {
    case 'help':
      for (const line of HELP_LINES) ports.pushSystemLine(line);
      return;
    case 'rom':
      ports.setRom(!ports.currentIsRom);
      return;
    case 'clear':
      ports.clearChatLog();
      return;
    case 'imagepaste':
      if (ports.imagepasteStart === null) {
        ports.pushSystemLine('画像貼り付けの権限がありません');
        return;
      }
      ports.imagepasteStart();
      return;
  }
};
